/**
 * Maps a tenant's stored theme onto the page. Two consumers:
 *  - the page's CSS variables, written by the design system's `applyTheme`: the contract's token
 *    set plus the design system's own `--theme-*` set (rarity, status, shape, motion), each from an
 *    allowlist, never a tenant-supplied name
 *  - the LootTheme prop for <LootProvider> (--los-* vars inside the opener)
 *
 * The sanitizing resolvers live in `@loot-os/api-contract`, shared with the dashboard's box
 * preview, which scopes a tenant theme to one container instead of the whole page. What stays
 * here is the bridge to the opener's own theme API and the cookie banner's.
 */
import {
  type CookieBannerTheme,
  resolveCookieBannerThemeVars,
  resolveOpenerTheme,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import { applyTheme } from "@loot-os/design-system/foundations/applyTheme";
import type { LootTheme } from "@loot-os/react";

/**
 * Applies the tenant theme on <html>, replacing a previous tenant's. `applyTheme` writes the
 * complete variable set, the body font (a system stack when the theme names none), `color-scheme`
 * (so native chrome follows a light palette) and loads the theme's catalog fonts. Called before
 * React renders, from the cached config, so the first paint is branded; the app's ThemeProvider
 * writes the same set again. With no theme it writes the stock palette, which `main.tsx` does at
 * boot so the splash and the store-not-found page have colors before any config arrives.
 */
export function applyPageTheme(theme: StorefrontTheme | undefined): void {
  applyTheme(document.documentElement, theme);
}

/**
 * Writes the cookie banner's theme onto <html> as `--cookie-banner-*`, beside the page theme:
 * `termly.css` maps them onto Termly's banner and preference modal. Always the complete set,
 * with an unset token as `var(--<store token>)`, so a tenant with no banner theme keeps getting
 * a banner that follows the store, and a previous tenant's values never survive.
 */
export function applyCookieBannerTheme(theme: CookieBannerTheme | undefined | null): void {
  const style = document.documentElement.style;
  const { colors, radius, buttonRadius } = resolveCookieBannerThemeVars(theme);
  for (const [name, value] of Object.entries(colors)) {
    style.setProperty(name, value);
  }
  style.setProperty("--cookie-banner-radius", radius);
  style.setProperty("--cookie-banner-button-radius", buttonRadius);
}

/**
 * The same stored shape drives the opener. The token → opener-color mapping
 * lives in api-contract, shared with the dashboard's box preview, which mounts
 * the same opener and would otherwise drift from this one.
 */
export function toLootTheme(theme: StorefrontTheme | undefined): LootTheme | undefined {
  return resolveOpenerTheme(theme);
}
